import { useEffect, useRef } from "react";

/**
 * 苹果液态玻璃的动态镜面高光：
 * 鼠标（或触摸）移动时，一道柔和的反光点在玻璃表面跟随，
 * 离开后立即淡出。模拟真实玻璃随视角变化的反光。
 *
 * 通过 CSS 变量 --spec-x / --spec-y 驱动 .glass-live::after 的 radial-gradient。
 */
export function useSpecular<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--spec-x", `${x}%`);
        el.style.setProperty("--spec-y", `${y}%`);
        el.style.setProperty("--spec-o", "1");
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      el.style.setProperty("--spec-o", "0");
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return ref;
}
